// Frontend error handling and recovery utilities

// Error types and classifications
export const ERROR_TYPES = {
  NETWORK: 'NETWORK',
  SERVER: 'SERVER',
  CLIENT: 'CLIENT',
  AUTHENTICATION: 'AUTHENTICATION',
  VALIDATION: 'VALIDATION',
  STORAGE: 'STORAGE',
  UNKNOWN: 'UNKNOWN'
};

export const ERROR_SEVERITY = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL'
};

// Circuit breaker for frontend API calls
class FrontendCircuitBreaker {
  constructor(name, options = {}) {
    this.name = name;
    this.failureThreshold = options.failureThreshold || 5;
    this.recoveryTimeout = options.recoveryTimeout || 30000; // 30 seconds
    this.monitoringPeriod = options.monitoringPeriod || 60000; // 1 minute
    
    this.failureCount = 0;
    this.lastFailureTime = null;
    this.nextAttemptTime = null;
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.successCount = 0;
  }

  recordSuccess() {
    this.failureCount = 0;
    this.successCount++;
    this.lastFailureTime = null;
    this.nextAttemptTime = null;
    this.state = 'CLOSED';
  }

  recordFailure() {
    this.failureCount++;
    this.successCount = 0;
    this.lastFailureTime = Date.now();
    
    if (this.failureCount >= this.failureThreshold) {
      this.state = 'OPEN';
      this.nextAttemptTime = Date.now() + this.recoveryTimeout;
      console.warn(`Circuit breaker OPENED for ${this.name} after ${this.failureCount} failures`);
    }
  }

  canExecute() {
    if (this.state === 'CLOSED') {
      return true;
    }
    
    if (this.state === 'OPEN') {
      if (Date.now() >= this.nextAttemptTime) {
        this.state = 'HALF_OPEN';
        console.log(`Circuit breaker entering HALF_OPEN state for ${this.name}`);
        return true;
      }
      return false;
    }
    
    // HALF_OPEN state - allow one attempt
    return true;
  }

  getState() {
    if (this.state === 'OPEN' && Date.now() >= this.nextAttemptTime) {
      this.state = 'HALF_OPEN';
    }
    return this.state;
  }

  reset() {
    this.failureCount = 0;
    this.successCount = 0;
    this.lastFailureTime = null;
    this.nextAttemptTime = null;
    this.state = 'CLOSED';
  }
}

// Error classification and handling
export class ErrorHandler {
  constructor() {
    this.circuitBreakers = new Map();
    this.retryConfig = {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 30000,
      backoffMultiplier: 2
    };
  }

  // Get or create circuit breaker for a service
  getCircuitBreaker(serviceName) {
    if (!this.circuitBreakers.has(serviceName)) {
      this.circuitBreakers.set(serviceName, new FrontendCircuitBreaker(serviceName));
    }
    return this.circuitBreakers.get(serviceName);
  }

  // Classify error based on response and error details
  classifyError(error) {
    if (!error) {
      return {
        type: ERROR_TYPES.UNKNOWN,
        severity: ERROR_SEVERITY.LOW,
        retryable: false,
        userMessage: 'An unknown error occurred'
      };
    }

    // Network errors
    if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
      return {
        type: ERROR_TYPES.NETWORK,
        severity: ERROR_SEVERITY.HIGH,
        retryable: true,
        userMessage: 'Network connection failed. Please check your internet connection and try again.',
        fallbackToLocal: true
      };
    }

    // HTTP status code based classification
    if (error.response?.status) {
      const status = error.response.status;
      
      if (status === 401) {
        return {
          type: ERROR_TYPES.AUTHENTICATION,
          severity: ERROR_SEVERITY.CRITICAL,
          retryable: false,
          userMessage: 'Authentication failed. Please log in again.',
          requiresReauth: true
        };
      }
      
      if (status === 403) {
        return {
          type: ERROR_TYPES.AUTHENTICATION,
          severity: ERROR_SEVERITY.HIGH,
          retryable: false,
          userMessage: 'Access denied. You do not have permission to perform this action.'
        };
      }
      
      if (status === 404) {
        return {
          type: ERROR_TYPES.CLIENT,
          severity: ERROR_SEVERITY.MEDIUM,
          retryable: false,
          userMessage: 'The requested resource was not found.'
        };
      }
      
      if (status === 429) {
        return {
          type: ERROR_TYPES.SERVER,
          severity: ERROR_SEVERITY.MEDIUM,
          retryable: true,
          userMessage: 'Too many requests. Please wait a moment before trying again.',
          retryDelay: 5000
        };
      }
      
      if (status >= 500) {
        return {
          type: ERROR_TYPES.SERVER,
          severity: ERROR_SEVERITY.HIGH,
          retryable: true,
          userMessage: 'Server error occurred. Please try again in a moment.',
          fallbackToLocal: true
        };
      }
      
      if (status >= 400) {
        return {
          type: ERROR_TYPES.CLIENT,
          severity: ERROR_SEVERITY.MEDIUM,
          retryable: false,
          userMessage: error.response.data?.error || 'Invalid request. Please check your input.'
        };
      }
    }

    // Timeout errors
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        type: ERROR_TYPES.NETWORK,
        severity: ERROR_SEVERITY.MEDIUM,
        retryable: true,
        userMessage: 'Request timed out. Please try again.',
        fallbackToLocal: true
      };
    }

    // Connection errors
    if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND') {
      return {
        type: ERROR_TYPES.NETWORK,
        severity: ERROR_SEVERITY.HIGH,
        retryable: true,
        userMessage: 'Cannot connect to server. Please check your connection.',
        fallbackToLocal: true
      };
    }

    // Default classification
    return {
      type: ERROR_TYPES.UNKNOWN,
      severity: ERROR_SEVERITY.MEDIUM,
      retryable: false,
      userMessage: error.message || 'An unexpected error occurred. Please try again.'
    };
  }

  // Execute operation with retry logic and circuit breaker
  async executeWithRetry(operation, serviceName, options = {}) {
    const config = { ...this.retryConfig, ...options };
    const circuitBreaker = this.getCircuitBreaker(serviceName);
    
    // Check circuit breaker
    if (!circuitBreaker.canExecute()) {
      const error = new Error(`Service ${serviceName} is temporarily unavailable`);
      error.circuitBreakerOpen = true;
      throw error;
    }

    let lastError;
    
    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
      try {
        const result = await operation();
        circuitBreaker.recordSuccess();
        return result;
      } catch (error) {
        lastError = error;
        const classified = this.classifyError(error);
        
        // Don't retry if not retryable or if we've exceeded max attempts
        if (!classified.retryable || attempt >= config.maxRetries) {
          circuitBreaker.recordFailure();
          break;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          config.baseDelay * Math.pow(config.backoffMultiplier, attempt),
          config.maxDelay
        );
        
        console.log(`Retrying ${serviceName} operation in ${delay}ms (attempt ${attempt + 1}/${config.maxRetries + 1})`);
        await this.sleep(delay);
      }
    }
    
    circuitBreaker.recordFailure();
    throw lastError;
  }

  // Execute with error handling and retry logic
  async executeWithFallback(primaryOperation, fallbackOperation, serviceName, options = {}) {
    try {
      return await this.executeWithRetry(primaryOperation, serviceName, options);
    } catch (error) {
      const classified = this.classifyError(error);
      
      if (classified.fallbackToLocal && fallbackOperation) {
        console.warn(`Primary operation failed for ${serviceName}, falling back to local storage:`, error.message);
        try {
          const result = await fallbackOperation();
          return {
            ...result,
            isFromFallback: true,
            fallbackReason: classified.userMessage
          };
        } catch (fallbackError) {
          console.error(`Fallback operation also failed for ${serviceName}:`, fallbackError.message);
          throw error; // Throw original error
        }
      }
      
      throw error;
    }
  }

  // Utility function for sleep
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Get circuit breaker status for monitoring
  getCircuitBreakerStatus() {
    const status = {};
    for (const [serviceName, circuitBreaker] of this.circuitBreakers) {
      status[serviceName] = {
        state: circuitBreaker.getState(),
        failureCount: circuitBreaker.failureCount,
        successCount: circuitBreaker.successCount,
        lastFailureTime: circuitBreaker.lastFailureTime
      };
    }
    return status;
  }

  // Reset circuit breaker (for manual recovery)
  resetCircuitBreaker(serviceName) {
    const circuitBreaker = this.circuitBreakers.get(serviceName);
    if (circuitBreaker) {
      circuitBreaker.reset();
      console.log(`Circuit breaker reset for ${serviceName}`);
    }
  }

  // Reset all circuit breakers
  resetAllCircuitBreakers() {
    for (const [, circuitBreaker] of this.circuitBreakers) {
      circuitBreaker.reset();
    }
    console.log('All circuit breakers reset');
  }
}



// User-friendly error messages
export const ERROR_MESSAGES = {
  NETWORK_UNAVAILABLE: 'Unable to connect to the server. Your conversations are being saved locally and will sync when connection is restored.',
  SERVER_ERROR: 'The server is experiencing issues. Please try again in a few moments.',
  AUTHENTICATION_REQUIRED: 'Your session has expired. Please log in again.',
  RATE_LIMITED: 'Too many requests. Please wait a moment before trying again.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
  CIRCUIT_BREAKER_OPEN: 'Service is temporarily unavailable. Please try again later.',
  FALLBACK_ACTIVE: 'Working in offline mode. Changes will sync when connection is restored.'
};

// Create singleton instances
export const errorHandler = new ErrorHandler();

// Utility functions for common error scenarios
export const handleApiError = (error, context = '') => {
  const classified = errorHandler.classifyError(error);
  console.error(`API Error ${context}:`, {
    type: classified.type,
    severity: classified.severity,
    message: classified.userMessage,
    originalError: error.message
  });
  return classified;
};

export const showUserFriendlyError = (error, context = '') => {
  const classified = handleApiError(error, context);
  return classified.userMessage;
};

// Recovery strategies
export const RECOVERY_STRATEGIES = {
  RETRY: 'retry',
  FALLBACK_LOCAL: 'fallback_local',
  REAUTH: 'reauth',
  MANUAL_INTERVENTION: 'manual_intervention',
  IGNORE: 'ignore'
};

export const getRecoveryStrategy = (error) => {
  const classified = errorHandler.classifyError(error);
  
  if (classified.requiresReauth) {
    return RECOVERY_STRATEGIES.REAUTH;
  }
  
  if (classified.fallbackToLocal) {
    return RECOVERY_STRATEGIES.FALLBACK_LOCAL;
  }
  
  if (classified.retryable) {
    return RECOVERY_STRATEGIES.RETRY;
  }
  
  if (classified.severity === ERROR_SEVERITY.CRITICAL) {
    return RECOVERY_STRATEGIES.MANUAL_INTERVENTION;
  }
  
  return RECOVERY_STRATEGIES.IGNORE;
};